import { useState, useEffect } from "react";
import AdminLayout from "../../layouts/AdminLayout";
import API from "../../services/api";

// ── DESIGN SYSTEM ────────────────────────────────────────────────────────────
const DS = {
  indigo:       "#4F46E5",
  indigoMid:    "#6366F1",
  indigoLight:  "#EEF2FF",
  indigoDark:   "#3730A3",
  teal:         "#0D9488",
  tealLight:    "#F0FDFA",
  emerald:      "#059669",
  emeraldLight: "#ECFDF5",
  amber:        "#D97706",
  amberLight:   "#FFFBEB",
  rose:         "#E11D48",
  roseLight:    "#FFF1F2",
  violet:       "#7C3AED",
  violetLight:  "#F5F3FF",
  orange:       "#EA580C",
  orangeLight:  "#FFF7ED",
  bg:           "#F8FAFC",
  surface:      "#FFFFFF",
  surfaceAlt:   "#F8FAFC",
  border:       "#E2E8F0",
  dark:         "#0F172A",
  gray:         "#64748B",
  grayLight:    "#F1F5F9",
  faint:        "#94A3B8",
};

const modules = [
  "Dashboard", "Role Master", "User Master", "Phase Master", "Task Master",
  "Access Control", "Project Assignment", "Report Category", "Discussion Topic",
  "Project Reports", "Report Approval", "Work Schedule", "Discussion Module", "Audit Logs"
];

export default function AccessControl() {
  const [rolesList, setRolesList] = useState([]);
  const [selectedRole, setSelectedRole] = useState("Manager");
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(false);
  const [hov, setHov] = useState(null);

  // Fetch Roles
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const res = await API.get("/admin/roles");
        if (res.data?.success) {
          const list = res.data.data || [];
          setRolesList(list);
          if (list.length > 0) {
            // Find if Manager exists, else set first
            const hasManager = list.find(r => r.title === "Manager");
            setSelectedRole(hasManager ? "Manager" : list[0].title);
          }
        }
      } catch (err) {
        console.error("Roles fetch failed:", err);
      }
    };
    fetchRoles();
  }, []);

  // Fetch Permissions for Selected Role
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("token");
        const res = await API.get(`/admin/permissions/${selectedRole}`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.data.success && res.data.data) {
          setPermissions(res.data.data);
        } else {
          setPermissions(modules.reduce((acc, m) => ({ ...acc, [m]: false }), {}));
        }
      } catch (err) {
        setPermissions(modules.reduce((acc, m) => ({ ...acc, [m]: true }), {}));
      } finally {
        setLoading(false);
      }
    };
    fetchPermissions();
  }, [selectedRole]);

  const togglePermission = (module) => {
    setPermissions((prev) => ({ ...prev, [module]: !prev[module] }));
  };

  const handleSelectAll = () => {
    setPermissions(modules.reduce((acc, m) => ({ ...acc, [m]: true }), {}));
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem("token");
      await API.post("/admin/permissions", { role: selectedRole, permissions }, { headers: { Authorization: `Bearer ${token}` } });
      alert(`Permissions saved successfully for ${selectedRole}`);
    } catch (err) {
      alert("Failed syncing permissions constraints to server.");
    }
  };

  return (
    <AdminLayout>
      <div style={{ flex: 1, padding: "28px 28px", overflowY: "auto" }}>
        
        {/* PAGE HEADER */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 26 }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: DS.indigoLight, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>🔐</div>
          <div>
            <h1 style={{ fontFamily: "'Inter',sans-serif", fontWeight: 800, fontSize: 26, color: DS.dark, margin: 0 }}>Access Control</h1>
            <div style={{ fontSize: 13, color: DS.gray, marginTop: 4 }}>Configure module access level clearance parameters for system roles</div>
          </div>
        </div>

        {/* SELECT ROLE CARD */}
        <div style={{ background: DS.surface, borderRadius: 18, border: `1.5px solid ${DS.border}`, overflow: "hidden", marginBottom: 24, boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
          <div style={{ height: 4, background: "linear-gradient(90deg,#4F46E5,#E11D48,#D97706)" }} />
          <div style={{ padding: "22px 26px" }}>
            <label style={{ fontSize: 12, fontWeight: 700, color: DS.gray, display: "block", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>Select Target Role</label>
            <select value={selectedRole} onChange={(e) => setSelectedRole(e.target.value)}
              style={{ width: "100%", maxWidth: "340px", borderRadius: 12, border: `1.5px solid ${DS.border}`, padding: "11px 14px", fontSize: 14, color: DS.dark, background: DS.surfaceAlt, outline: "none", fontFamily: "inherit", boxSizing: "border-box", height: "43px" }}
              onFocus={e => e.target.style.borderColor = DS.indigo} onBlur={e => e.target.style.borderColor = DS.border}>
              {rolesList.filter(r => r.title).map((r) => (
                <option key={r.id} value={r.title}>{r.title}</option>
              ))}
            </select>
          </div>
        </div>

        {/* MODULE PERMISSIONS CARD */}
        <div style={{ background: DS.surface, borderRadius: 18, border: `1.5px solid ${DS.border}`, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
          <div style={{ height: 4, background: "linear-gradient(90deg,#4F46E5,#E11D48,#D97706)" }} />
          
          <div style={{ padding: "18px 22px", borderBottom: `1px solid ${DS.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
            <div>
              <div style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: 16, color: DS.dark }}>Module Permissions</div>
              <div style={{ fontSize: 12, color: DS.gray, marginTop: 2 }}>Define active layout modules for the "{selectedRole}" clearance level</div>
            </div>
            <button onClick={handleSelectAll}
              style={{ background: DS.indigoLight, color: DS.indigo, border: `1px solid #C7D2FE`, padding: "8px 18px", borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s" }}
              onMouseEnter={e => { e.currentTarget.style.background = DS.indigo; e.currentTarget.style.color = "#fff"; }}
              onMouseLeave={e => { e.currentTarget.style.background = DS.indigoLight; e.currentTarget.style.color = DS.indigo; }}>
              Select All Modules
            </button>
          </div>

          <div style={{ padding: "26px" }}>
            {loading ? (
              <div style={{ padding: "40px", textAlign: "center", color: DS.faint, fontSize: 14 }}>Loading access control parameters...</div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
                {modules.map((module) => {
                  const isChecked = !!permissions[module];
                  return (
                    <div key={module} onMouseEnter={() => setHov(module)} onMouseLeave={() => setHov(null)}
                      style={{ display: "flex", alignItems: "center", justifyContent: "space-between", border: `1.5px solid ${DS.border}`, borderRadius: 16, padding: "14px 18px", background: hov === module ? DS.surfaceAlt : DS.surface, transition: "all 0.15s" }}>
                      
                      <span style={{ fontSize: 14, fontWeight: 700, color: DS.dark }}>{module}</span>
                      
                      {/* Custom Slide Toggle Switch */}
                      <button type="button" onClick={() => togglePermission(module)}
                        style={{ width: 48, height: 26, borderRadius: 100, background: isChecked ? DS.emerald : DS.faint, border: "none", position: "relative", cursor: "pointer", transition: "background 0.2s", padding: 0, outline: "none" }}>
                        <div style={{ width: 20, height: 20, borderRadius: "50%", background: "#fff", position: "absolute", top: 3, left: isChecked ? 25 : 3, transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            <div style={{ marginTop: 28, borderTop: `1px solid ${DS.border}`, paddingTop: 20, display: "flex", justifyContent: "flex-start" }}>
              <button onClick={handleSave}
                style={{ background: "linear-gradient(135deg,#4F46E5,#6366F1)", color: "#fff", border: "none", padding: "12px 28px", borderRadius: 12, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", boxShadow: "0 4px 14px rgba(79,70,229,0.3)" }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = "0 6px 20px rgba(79,70,229,0.45)"}
                onMouseLeave={e => e.currentTarget.style.boxShadow = "0 4px 14px rgba(79,70,229,0.3)"}>
                Save Permissions Schema
              </button>
            </div>
          </div>
        </div>

      </div>
    </AdminLayout>
  );
}